import { createClient as createSupabaseClient } from "@supabase/supabase-js";
import type { SupabaseClient } from "@supabase/supabase-js";
import { Database } from "./types";
import { syncSupabaseSessionToServer } from "./sync-server-session";
import { ADMIN_LOGIN_FLOW_SESSION_KEY } from "@/lib/auth/admin-login-flow";

export type { SupabaseClient };

/**
 * Browser-only Supabase client (localStorage session, single refresh loop per tab).
 * Server code must use `@/lib/supabase/server`.
 *
 * Session cookies for middleware/API are mirrored via `/api/auth/sync-session` so Next.js
 * middleware still sees the user without storing auth in document.cookie from JS.
 */
let browserClient: SupabaseClient<Database> | null = null;
let authListenerRegistered = false;

const shouldLogAuth =
  process.env.NODE_ENV === "development" || process.env.NEXT_PUBLIC_SUPABASE_DEBUG_AUTH === "true";

function attachGlobalAuthListener(client: SupabaseClient<Database>): void {
  if (authListenerRegistered) return;
  authListenerRegistered = true;

  let refreshSyncTimer: ReturnType<typeof setTimeout> | null = null;

  client.auth.onAuthStateChange((event, session) => {
    if (shouldLogAuth) {
      if (event === "TOKEN_REFRESHED") {
        console.log("[Supabase Auth]", event, { userId: session?.user?.id });
      } else if (event === "SIGNED_OUT" || event === "USER_UPDATED") {
        console.log("[Supabase Auth]", event, session?.user?.id ?? "no-session");
      } else {
        console.log("[Supabase Auth]", event, session?.user?.id ?? "no-session");
      }
    }

    if (event === "SIGNED_IN") {
      void syncSupabaseSessionToServer(session);
      return;
    }

    if (event === "TOKEN_REFRESHED") {
      if (refreshSyncTimer) clearTimeout(refreshSyncTimer);
      refreshSyncTimer = setTimeout(() => {
        void syncSupabaseSessionToServer(session);
      }, 400);
      return;
    }

    if (event === "SIGNED_OUT") {
      void fetch("/api/auth/clear-session", { method: "POST", credentials: "same-origin" }).catch(
        () => {}
      );
      // Unexpected sign-out (e.g. revoked refresh); explicit logout sets explicit_logout and navigates to /
      if (typeof window !== "undefined") {
        const explicit = sessionStorage.getItem("explicit_logout") === "1";
        // Admin login form: signOut after wrong role or transient errors — stay on /admin/login, no /login redirect.
        if (sessionStorage.getItem(ADMIN_LOGIN_FLOW_SESSION_KEY) === "1") {
          return;
        }
        if (!explicit) {
          const path = window.location.pathname || "";
          if (path === "/admin/login" || path.startsWith("/admin/login/")) {
            return;
          }
          // Password recovery pages drive their own navigation (signOut is part of the
          // flow, not a session expiry). Don't auto-redirect to /login here, otherwise
          // the listener stomps on /reset-password's own success/error handling.
          if (
            path === "/reset-password" ||
            path.startsWith("/reset-password/") ||
            path === "/forgot-password" ||
            path.startsWith("/forgot-password/") ||
            path === "/auth/auth-code-error" ||
            path.startsWith("/auth/")
          ) {
            return;
          }
          if (path.startsWith("/admin")) {
            window.location.assign("/admin/login?reason=session_expired");
          } else {
            window.location.assign("/login");
          }
        }
      }
    }
  });
}

export function createClient(): SupabaseClient<Database> {
  const supabaseUrl =
    process.env.NEXT_PUBLIC_SUPABASE_URL || "https://placeholder.supabase.co";
  const supabaseAnonKey =
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBsYWNlaG9sZGVyIiwicm9sZSI6ImFub24iLCJpYXQiOjE2NDUxOTI4MDAsImV4cCI6MTk2MDc2ODgwMH0.placeholder";

  // SSR / accidental import: no storage (hooks still run only on client).
  if (typeof window === "undefined") {
    return createSupabaseClient<Database>(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
      },
      realtime: { params: { eventsPerSecond: 10 } },
      global: { headers: { "X-Client-Info": "rentitforward-web-ssr" } },
    });
  }

  if (browserClient) {
    return browserClient;
  }

  try {
    new URL(supabaseUrl);
  } catch {
    console.warn("Invalid Supabase URL, using placeholder:", supabaseUrl);
  }

  browserClient = createSupabaseClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: "pkce",
      storage: window.localStorage,
    },
    realtime: {
      params: { eventsPerSecond: 10 },
    },
    global: {
      headers: { "X-Client-Info": "rentitforward-web" },
    },
  });

  attachGlobalAuthListener(browserClient);

  return browserClient;
}
